import React from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';

export type MessageGroupEditorProps = {
  groups: string[];
  value: Record<string, string[]>;
  onChange: (value: Record<string, string[]>) => void;
  disabled?: boolean;
  maxMessagesPerGroup?: number | Partial<Record<string, number>>;
  groupDescriptions?: Record<string, string>;
  labels?: {
    helperText?: string;
    emptyGroupText?: string;
    addMessageLabel?: string;
    messagePlaceholder?: string;
    groupLabels?: Record<string, string>;
    deleteMessageAriaLabel?: (group: string, index: number) => string;
  };
};

export const MessageGroupEditor: React.FC<MessageGroupEditorProps> = ({
  groups,
  value,
  onChange,
  disabled = false,
  maxMessagesPerGroup,
  groupDescriptions,
  labels,
}) => {
  const selectableTextSx = {
    userSelect: 'text',
    WebkitUserSelect: 'text',
    cursor: 'text',
  } as const;

  const getMaxMessages = (group: string) =>
    typeof maxMessagesPerGroup === 'number' ? maxMessagesPerGroup : maxMessagesPerGroup?.[group];

  const updateMessage = (group: string, index: number, nextValue: string) => {
    const groupMessages = value[group] ?? [];
    const updated = [...groupMessages];
    updated[index] = nextValue;
    onChange({ ...value, [group]: updated });
  };

  const addMessage = (group: string) => {
    const groupMessages = value[group] ?? [];
    const maxMessages = getMaxMessages(group);
    if (typeof maxMessages === 'number' && groupMessages.length >= maxMessages) {
      return;
    }
    onChange({ ...value, [group]: [...groupMessages, ''] });
  };

  const removeMessage = (group: string, index: number) => {
    const groupMessages = value[group] ?? [];
    onChange({
      ...value,
      [group]: groupMessages.filter((_, messageIndex) => messageIndex !== index),
    });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={selectableTextSx}>
        {labels?.helperText ??
          'Each group stores an array of strings. You can keep it empty or add multiple message variants.'}
      </Typography>
      {groups.map((group) => {
        const messages = value[group] ?? [];
        const maxMessages = getMaxMessages(group);
        const canAddMessage =
          !disabled && (typeof maxMessages !== 'number' || messages.length < maxMessages);

        return (
          <Box
            key={group}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 2,
            }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={selectableTextSx}>
                {labels?.groupLabels?.[group] ?? group}
              </Typography>
              {groupDescriptions?.[group] && (
                <Typography variant="caption" color="text.secondary" sx={selectableTextSx}>
                  {groupDescriptions[group]}
                </Typography>
              )}

              {messages.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={selectableTextSx}>
                  {labels?.emptyGroupText ??
                    'No messages yet. Add one or more strings for this group.'}
                </Typography>
              )}

              {messages.map((message, index) => (
                <Stack direction="row" spacing={1} key={`${group}-${index}`} alignItems="center">
                  <CustomTextField
                    fullWidth
                    size="small"
                    value={message}
                    disabled={disabled}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      updateMessage(group, index, event.target.value)
                    }
                    placeholder={labels?.messagePlaceholder ?? 'Message text'}
                  />
                  <IconButton
                    aria-label={
                      labels?.deleteMessageAriaLabel?.(group, index + 1) ??
                      `Delete ${group} message ${index + 1}`
                    }
                    onClick={() => removeMessage(group, index)}
                    disabled={disabled}
                    size="small">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}

              {canAddMessage && (
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addMessage(group)}>
                    {labels?.addMessageLabel ?? 'Add message'}
                  </Button>
                </Box>
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};
